// @ts-nocheck
/**
 * Author: Ben Quackenbush
 * Date: 12/29/2025
 * Description: A Web Component that creates variant option buttons for the Klevu search landing page.
 *    Waits for product information to be fetched from Shopify GraphQL before rendering.
 *    After rendering, updates the price element and the add to cart button.
 *    Listens for option changes and updates the price and the add to cart button.
 *
 */
import { Component } from '@theme/component';
class TWKlevuVariantPicker extends Component {
  constructor() {
    super();
    this.productInfo = null;
    this.itemGroupId = null;
    this.productsInfoHandler = null;
    this.variantsLength = 0;
    this.optionsSelected = {};
    this.selectedVariant = null;
    this.selectedVariantId = null;
  }
  connectedCallback() {
    this.itemGroupId = this.getAttribute('data-item-group-id');
    if (window.productsInfo) {
      this.productInfo = window.productsInfo[this.itemGroupId] || null;
      this.variantsLength = this.productInfo?.variants?.nodes?.length || 0;
    }
    if (!this.productInfo) {
      this.productsInfoHandler = () => {
        this.productInfo = window.productsInfo[this.itemGroupId];
        this.variantsLength = this.productInfo?.variants?.nodes?.length || 0;
        if (this.productInfo) {
          document.removeEventListener(
            'productsInfoLoaded',
            this.productsInfoHandler,
          );
          this.productsInfoHandler = null;
          this.render();
        }
      };
      document.addEventListener('productsInfoLoaded', this.productsInfoHandler);
      return;
    }
    this.render();
  }
  disconnectedCallback() {
    if (!this.productsInfoHandler) return;
    document.removeEventListener(
      'productsInfoLoaded',
      this.productsInfoHandler,
    );
  }
  createAndSetOptions() {
    let html = '';
    const options = this.productInfo?.options;
    if (!options?.length) return html;
    options.forEach((option, index) => {
      if (!this.optionsSelected[option.name]) {
        this.optionsSelected[option.name] = option.optionValues[0].name;
        if (this.variantsLength > 1) {
          html += createOptionLabel(
            option.name,
            option.optionValues[0].name,
            this.itemGroupId,
            index,
          );
        }
      }
      if (!this.variantsLength || this.variantsLength === 1) return;
      html += createOptionRow(
        option.optionValues,
        option.name,
        this.itemGroupId,
      );
    });

    return html;
  }
  updateOptionLabel() {
    if (this.variantsLength <= 1) return;
    for (const optionLabel in this.optionsSelected) {
      const dashedString = optionLabel.trim().replaceAll(' ', '-');
      const labelValEl = this.querySelector(
        `#${dashedString}-${this.itemGroupId} .option-label-value`,
      );
      if (!labelValEl) {
        console.error(
          'No option label to update',
          `#${optionLabel}-${this.itemGroupId}`,
        );
        continue;
      }
      labelValEl.textContent = this.optionsSelected[optionLabel];
    }
  }
  findVariant() {
    const variants = this.productInfo.variants.nodes;
    const selectedVariant = variants.find((variant) => {
      const optionsMatch = variant.selectedOptions.every((option) => {
        const { name, value } = option;
        return this.optionsSelected[name] === value;
      });
      return optionsMatch;
    });
    this.selectedVariant = selectedVariant;
    let selectedVariantId = null;
    if (selectedVariant) {
      selectedVariantId = selectedVariant.id.split('/').pop();
    }
    this.selectedVariantId = selectedVariantId;
  }
  variantHandler = (event) => {
    const element = event.target;
    const optionName = element.dataset.optionName;
    const rowButtons = this.querySelectorAll(
      `.optionSwiper button[data-option-name="${optionName}"]`,
    );
    for (const button of rowButtons) {
      button.classList.remove('active');
    }
    element.classList.add('active');
    const value = element.value;
    this.optionsSelected[optionName] = value;
    this.findVariant();
    this.updateATCButton();
    this.updatePrice();
    this.updateOptionLabel();
    this.updateImage();
  };
  addVariantEventListeners() {
    const buttons = this.querySelectorAll('.optionSwiper button');
    this.optionsSelected;
    for (const button of buttons) {
      button.addEventListener('click', this.variantHandler);
    }
  }
  updateATCButton() {
    const addToCartForm = document.querySelector(
      `form[data-item-group-id="${this.itemGroupId}"]`,
    );
    const addToCartButton = addToCartForm.querySelector('button');
    const addToCartButtonText = addToCartForm.querySelector(
      '.add-to-cart-text__content',
    );
    const variantInput = addToCartForm.querySelector('[ref="variantId"]');
    if (!this.selectedVariant) {
      addToCartButton.disabled = true;
      addToCartButtonText.textContent = 'Invalid selected options';
      return;
    }
    variantInput.value = this.selectedVariantId;
    const { availableForSale, currentlyNotInStock, stopSelling } =
      this.selectedVariant;
    // A variant-level custom.stop_selling metafield forces the variant to
    // appear sold out even when it is available for sale.
    const isStopSelling = stopSelling?.value === 'true';
    // need to look into using text from the locales json files.
    if (isStopSelling) {
      addToCartButton.disabled = true;
      addToCartButtonText.textContent = 'Sold out';
      return;
    }
    if (availableForSale && currentlyNotInStock) {
      addToCartButtonText.textContent = 'Available for backorder';
    } else if (availableForSale) {
      addToCartButtonText.textContent = 'Add to cart';
    } else {
      addToCartButton.disabled = true;
      addToCartButtonText.textContent = 'Sold out';
      return;
    }
    addToCartButton.disabled = false;
  }
  updatePrice() {
    if (!this.selectedVariant || this.variantsLength <= 1) return;
    const { price, compareAtPrice = {} } = this.selectedVariant;
    const priceContainer = document.querySelector(
      `.price-container[data-item-group-id="${this.itemGroupId}"]`,
    );
    let html = priceHTML(price);
    const compareAtPriceAmount = compareAtPrice?.amount || 0;
    const parsedPriceAmount = parseFloat(price.amount) || 0;
    const parsedCompareAtPriceAmount = parseFloat(compareAtPriceAmount);

    if (parsedCompareAtPriceAmount > parsedPriceAmount) {
      html = html + compareAtPriceHTML(compareAtPrice);
    }
    priceContainer.innerHTML = html;
  }
  updateImage() {
    if (!this.selectedVariant) return;
    const imageContainer = document.querySelector(
      `.klevuImgWrap[data-item-group-id="${this.itemGroupId}"] img`,
    );
    if (!imageContainer) return;
    const { url, altText } = this.selectedVariant.image || {};
    if (url) {
      imageContainer.src = url;
    }
    if (altText) {
      imageContainer.alt = altText;
    }
  }
  render() {
    if (!this.productInfo) return;
    this.innerHTML = this.createAndSetOptions();
    this.findVariant();
    this.updateATCButton();
    this.updatePrice();
    this.updateOptionLabel();
    this.updateImage();
    this.addVariantEventListeners();
  }
}
if (!customElements.get('tw-klevu-variant-picker')) {
  customElements.define('tw-klevu-variant-picker', TWKlevuVariantPicker);
}
const leftChevron = `
  <div class='optionSwiper-button-prev' aria-label='Previous'>    
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <g clip-path="url(#clip0_14984_left)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M15.7071 5.29289C15.3166 4.90237 14.6834 4.90237 14.2929 5.29289L8.29289 11.2929C7.90237 11.6834 7.90237 12.3166 8.29289 12.7071L14.2929 18.7071C14.6834 19.0976 15.3166 19.0976 15.7071 18.7071C16.0976 18.3166 16.0976 17.6834 15.7071 17.2929L10.4142 12L15.7071 6.70711C16.0976 6.31658 16.0976 5.68342 15.7071 5.29289Z" fill="#4D4D4D"/>
      </g>
      <defs>
        <clipPath id="clip0_14984_left">
          <rect width="24" height="24" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  </div>
`;
const rightChevron = `    
  <div class='optionSwiper-button-next' aria-label='Next'>
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <g clip-path="url(#clip0_14984_1235)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M8.29289 5.29289C8.68342 4.90237 9.31658 4.90237 9.70711 5.29289L15.7071 11.2929C16.0976 11.6834 16.0976 12.3166 15.7071 12.7071L9.70711 18.7071C9.31658 19.0976 8.68342 19.0976 8.29289 18.7071C7.90237 18.3166 7.90237 17.6834 8.29289 17.2929L13.5858 12L8.29289 6.70711C7.90237 6.31658 7.90237 5.68342 8.29289 5.29289Z" fill="#4D4D4D"/>
      </g>
      <defs>
        <clipPath id="clip0_14984_1235">
          <rect width="24" height="24" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  </div>
`;
const createOptionLabel = (name, value, itemGroupId, index) => {
  const dashedString = name.trim().replaceAll(' ', '-');
  return `
    <p id="${dashedString}-${itemGroupId}" class="${index > 0 ? 'mt-4' : 'mt-1'} mb-1" style="font-size: var(--font-paragraph--size);">
      <span class="font-medium">${name}: </span><span class="option-label-value">${value}</span>
    </p>`;
};
const createOptionRow = (optionValues, optionName) => {
  const row = `
    <div class="optionSwiper-container flex items-center">
      ${leftChevron}
      <div
        class='swiper optionSwiper'
        id='swiper-color'
      >
        <div class='swiper-wrapper'>
          ${createButtonSlides(optionValues, optionName)}
        </div>
      </div>
      ${rightChevron}
    </div>
  `;
  return row;
};
const swatchSlide = (funcOptions) => {
  const { values, index, optionName } = funcOptions;
  const { name, swatch } = values;
  const { color, image } = swatch;
  let background = '';
  // what does swatch background do? Is it needed?
  // let swatchBackground = `--swatch-background: ${color};`;
  if (image) {
    background = `background-image: url(${image.image.url})`;
  } else {
    background = `background-color:${color}`;
  }
  const swiperSlide = `
    <div class="swiper-slide variant-option-swatch">
      <div class="variant-option__button-label variant-option__button-label--has-swatch h-7">
        <button
            type="button"
            class="outer-swatch-border relative swatch ${index === 0 ? 'active' : ''}"
            title="${name}"
            value="${name}"
            data-option-name="${optionName}"
            style="width: 100%;background-size: 28px, 28px; border-radius:32px; ${background};"
        >
          <span class='inner-swatch-border'> </span>
        </button> 
      </div>
    </div>
  `;
  return swiperSlide;
};
const optionSlide = (funcOptions) => {
  const { values, index, optionName } = funcOptions;
  const { name } = values;
  const swiperSlide = `
    <div class="swiper-slide variant-option-button" >
      <div class="variant-option__button-label">
          <button
            type="button"
            class="outer-swatch-border rounded-sm px-2 py-1 relative ${index === 0 ? 'active' : ''}"
            title="${name}"
            value="${name}"
            data-option-name="${optionName}"
            style="font-size: var(--font-paragraph--size);"
          >
          ${name}
          </button>
      </div>
    </div>
  `;
  return swiperSlide;
};
const priceHTML = (price) => {
  const { amount, currencyCode } = price;
  const formatedPrice = formatCurrency(amount, currencyCode);
  return `
    <span role="group">
      <span class="visually-hidden">Sale price&nbsp;</span>
      <span class="price text-featured-two leading-featured-two font-medium"><span class="money" aria-label="Sale price is ${formatedPrice}">${formatedPrice}</span></span>
    </span>
  `;
};
const compareAtPriceHTML = (compareAtPrice) => {
  const { amount, currencyCode } = compareAtPrice;
  const formatedCompareAtPrice = formatCurrency(amount, currencyCode);
  return `
    <span role="group">
      <span class="text-foreground-neutral-faded text-body-three leading-body-three line-through"><span class="money" aria-label="Previous price was ${formatedCompareAtPrice}">${formatedCompareAtPrice}</span></span>
    </span>
  `;
};

const createButtonSlides = (optionValues, optionName) => {
  let buttonSlides = '';
  optionValues.forEach((values, index) => {
    const { swatch } = values;
    const funcOptions = { values, index, optionName };
    const swiperSlide = swatch
      ? swatchSlide(funcOptions)
      : optionSlide(funcOptions);
    buttonSlides = buttonSlides + swiperSlide;
  });
  return buttonSlides;
};
const formatCurrency = (amount, currencyCode, locale = 'en-US') => {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    });
    const formated = formatter.format(amount);
    const symbol = formated[0];
    const formatedAmount = formated.slice(1);
    return `${symbol} ${formatedAmount}`;
  } catch (error) {
    console.error(error);
    return `${currencyCode} ${parseFloat(amount).toFixed(2)}`;
  }
};
